import React, { useEffect, useState, useRef } from "react"
import { api } from "aldoo-ra/API"
import config from "aldoo-ra/Config"
import Spinner from "aldoo-ra/Spinner"
import Typography from "aldoo-ra/Typography"
import Button from "aldoo-ra/Button"
import { showSuccessToast, TOAST_POSITIONS } from "aldoo-ra/Toast"
import TextBundle from "aldoo-ra/CMS/text-bundle"
import Localization from "aldoo-ra/Localization"
import Tooltip from "aldoo-ra/Tooltip"
import {
  ArrowDownTrayIcon,
  DocumentIcon,
  ClipboardDocumentIcon,
  CheckIcon,
} from "@heroicons/react/24/outline"

const snapAPI = api("snap")

const formatSize = (bytes) => {
  if (!bytes) return ""
  const units = ["B", "KB", "MB", "GB"]
  let value = bytes
  let unit = 0
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024
    unit++
  }
  return `${value.toFixed(value >= 10 || unit === 0 ? 0 : 1)} ${units[unit]}`
}

export const downloadURL = (hash) =>
  `${config.PLATFORM_API_URL}/${config.PLATFORM_API_VERSION}/snap-dl/${hash}`

/**
 * Public viewer for a captured file — snap.aldoo.com/v/{hash}.
 * Google-drive-style: dark stage with the media centered, top bar with
 * title / size / download. Polls while the upload is still in flight.
 */
export default function SnapViewer() {
  const texts = TextBundle({ id: "snap_viewer" })
  const [item, setItem] = useState(null)
  const [error, setError] = useState(null)
  const [copied, setCopied] = useState(false)
  const pollRef = useRef(null)

  const hash =
    typeof window !== "undefined"
      ? window.location.pathname.match(/\/v\/([^/?#]+)/)?.[1]
      : null

  const resolve = async () => {
    if (!hash) return
    const res = await snapAPI({ resolve: { hash } }).catch(() => null)
    if (!res || res.error) {
      setError(res?.error || "not_found")
      return
    }
    setItem(res)
    // Link opened before the upload finished — poll until it lands.
    if (res.status === "pending" && !pollRef.current) {
      pollRef.current = setInterval(resolve, 2000)
    }
    if (res.status === "active" && pollRef.current) {
      clearInterval(pollRef.current)
      pollRef.current = null
    }
  }

  useEffect(() => {
    resolve()
    return () => pollRef.current && clearInterval(pollRef.current)
  }, [hash])

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href)
      setCopied(true)
      showSuccessToast(texts?.snap_viewer_link_copied_toast, "", { position: TOAST_POSITIONS.BOTTOM_CENTER })
      setTimeout(() => setCopied(false), 2000)
    } catch {}
  }

  if (error) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center gap-3">
        <Typography variant="h3">
          {texts?.snap_viewer_not_found_title}
        </Typography>
        <Typography variant="body1_dim">
          {texts?.snap_viewer_not_found_message}
        </Typography>
      </div>
    )
  }

  if (!item) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Spinner className="w-6 h-6" />
      </div>
    )
  }

  const uploading = item.status !== "active"

  return (
    <div className="h-screen flex flex-col bg-black/95 overflow-hidden">
      {/* Top bar */}
      <div className="flex items-center gap-3 px-4 py-3 text-white">
        <DocumentIcon className="w-5 h-5 shrink-0 opacity-70" />
        <div className="flex-1 min-w-0">
          <div className="truncate font-medium">{item.title || item.fileName || item.hash}</div>
          <div className="text-xs opacity-60">
            {[item.fileName, formatSize(item.size)].filter(Boolean).join(" · ")}
          </div>
        </div>
        <Tooltip content={texts?.snap_viewer_copy_link}>
          <Button
            variant="icon"
            onClick={copyLink}
            className="text-white/80 hover:text-white"
            aria-label={texts?.snap_viewer_copy_link}
          >
            {copied ? <CheckIcon className="w-5 h-5" /> : <ClipboardDocumentIcon className="w-5 h-5" />}
          </Button>
        </Tooltip>
        {!uploading && (
          <a
            href={downloadURL(item.hash)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors"
          >
            <ArrowDownTrayIcon className="w-4 h-4" />
            {texts?.snap_viewer_download}
          </a>
        )}
      </div>

      {/* Stage — min-h-0 so the media constrains to the viewport instead of
          stretching the page */}
      <div className="flex-1 min-h-0 flex items-center justify-center p-4">
        <SnapMediaStage item={item} uploading={uploading} />
      </div>

      {/* Branding footer */}
      <div className="py-3 text-center">
        <a
          href="/"
          className="text-xs text-white/40 hover:text-white/70 transition-colors"
        >
          {texts?.snap_viewer_powered_by} <span className="font-semibold">Aldoo Snap</span>
        </a>
      </div>
    </div>
  )
}

/**
 * The media stage of the viewer — image / video / audio / generic file.
 * Shared between the /v/{hash} page and the account gallery overlay.
 */
export function SnapMediaStage({
  item,
  uploading = false,
  maxHeightClass = "max-h-[calc(100vh-160px)]",
  // fill: media stretches to the whole stage (fullscreen lightbox) instead
  // of shrinking to viewport-minus-chrome with rounded corners.
  fill = false,
}) {
  const texts = TextBundle({ id: "snap_viewer" })
  const { template } = Localization()

  if (uploading) {
    return (
      <div className="flex flex-col items-center gap-4 text-white/80">
        <Spinner className="w-8 h-8" />
        <span>{texts?.snap_viewer_uploading}</span>
      </div>
    )
  }
  if (item.type === "image") {
    return (
      <img
        src={item.file}
        alt={item.title || item.fileName}
        className={
          fill
            ? "w-full h-full object-contain"
            : `max-w-full ${maxHeightClass} object-contain rounded-lg shadow-2xl`
        }
      />
    )
  }
  if (item.type === "video") {
    // Full-bleed: the player fills the whole stage (letterboxed by
    // object-contain) instead of shrinking to the file's pixel size.
    return (
      <video
        src={item.file}
        poster={item.thumbnail || undefined}
        controls
        autoPlay={false}
        className={
          fill
            ? "w-full h-full object-contain"
            : "w-full h-full max-h-[calc(100vh-120px)] object-contain"
        }
      />
    )
  }
  if (item.type === "audio") {
    return (
      <div className="w-full max-w-xl bg-white/5 rounded-2xl p-8 flex flex-col items-center gap-6">
        <Typography 
          variant="h3" 
          className="text-white truncate max-w-full"
        >
          {item.title || item.fileName}
        </Typography>
        <audio src={item.file} controls className="w-full" />
      </div>
    )
  }
  return (
    <div className="flex flex-col items-center gap-4 text-white/80">
      <DocumentIcon className="w-16 h-16 opacity-50" />
      <span>{item.fileName}</span>
      <a
        href={downloadURL(item.hash)}
        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white/10 hover:bg-white/20 text-white font-medium transition-colors"
      >
        <ArrowDownTrayIcon className="w-4 h-4" />
        {template(texts?.snap_viewer_download_with_size, { size: formatSize(item.size) })}
      </a>
    </div>
  )
}
